import {
  Container,
  Paper,
  Title,
  Text,
  Group,
  Stack,
  Badge,
  Button,
  Card,
  Table,
  Tabs,
  Textarea,
  Alert,
  NumberInput,
  Loader,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconFileText,
  IconUsers,
  IconAward,
  IconEdit,
  IconFileDescription,
  IconPackage,
  IconClipboardList,
  IconLockOpen,
  IconCurrencyDollar,
  IconDeviceFloppy,
  IconCheck,
} from "@tabler/icons-react";
import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { notifications } from "@mantine/notifications";
import { useTenderById, useUpdateTender, useUpdateStage } from "../hooks/useTenders";
import { useBidSubmissions } from "../hooks/useBidSubmissions";
import { useTenderItems } from "../hooks/useTenderItems";
import { useTenderRequirements } from "../hooks/useTenderRequirements";
import { useTechnicalEvaluations, useCreateBulkTechnicalEvaluations } from "../hooks/useTechnicalEvaluation";
import { useEvaluationCriteria } from "../hooks/useEvaluationCriteria";
import { useBidSubmissionScores, useSaveBidSubmissionScores, useBulkSaveEvaluationScores } from "../hooks/useEvaluationScores";
import { StageBadge } from "../components/StageBadge";
import { TorTab } from "../components/TorTab";
import { BidQualificationModal } from "../components/BidQualificationModal";
import { OverallEvaluationTab } from "../components/OverallEvaluationTab";
import { GenerateEvaluationReportModal } from "../components/GenerateEvaluationReportModal";
import { useEvaluationReport } from "../hooks/useEvaluationReports";
import dayjs from "dayjs";
import { formatCurrency } from '../../../utils/currency';

export function TenderDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: tender, isLoading, error, refetch } = useTenderById(id!);
  const { data: bidSubmissions } = useBidSubmissions(id!);
  const { data: tenderItems, isLoading: loadingItems } = useTenderItems(id!);
  const { data: tenderRequirements, isLoading: loadingRequirements } = useTenderRequirements(id!);
  const { data: technicalEvaluations, isLoading: loadingEvaluations, refetch: refetchEvaluations } = useTechnicalEvaluations(id!);
  const { data: evaluationCriteria, isLoading: loadingCriteria } = useEvaluationCriteria(id!);
  const createBulkEvaluations = useCreateBulkTechnicalEvaluations();
  const updateTender = useUpdateTender();
  const updateStage = useUpdateStage();
  const saveScores = useSaveBidSubmissionScores();
  const bulkSaveScores = useBulkSaveEvaluationScores();
  const [qualificationModalOpened, setQualificationModalOpened] = useState(false);
  const [selectedBidSubmission, setSelectedBidSubmission] = useState<any>(null);
  const [evaluatorEmails, setEvaluatorEmails] = useState("");
  const [isEditingEmails, setIsEditingEmails] = useState(false);
  const [activeTab, setActiveTab] = useState<string | null>("overview");
  const [generateReportModalOpened, setGenerateReportModalOpened] = useState(false);
  // State for managing scores in the Overall Evaluation tab
  const [scoreData, setScoreData] = useState<Record<string, Record<string, number>>>({});
  const { data: evaluationReport } = useEvaluationReport(id || '');

  // Check if tender is in OPENED stage or later (enables evaluation tabs)
  const canViewEvaluations = tender && ['OPENED', 'TECH_EVAL', 'FIN_EVAL', 'AWARDED'].includes(tender.stage);
  
  // Check if tender is in OPENED stage or later (enables bid review)
  const canReviewBids = tender && ['OPENED', 'TECH_EVAL', 'FIN_EVAL', 'AWARDED'].includes(tender.stage);

  // Check if tender can be opened
  // For PUBLISHED stage: must have opening date and it must have been reached
  // For COLLECTION stage: can be opened directly
  const canOpenTender = useMemo(() => {
    if (!tender) return false;
    
    // If in COLLECTION stage, can always be opened
    if (tender.stage === 'COLLECTION') return true;
    
    // If in PUBLISHED stage, check opening date
    if (tender.stage === 'PUBLISHED') {
      // Must have an opening date
      if (!tender.openingDate) return false;
      
      // Opening date must have been reached (current time >= opening date)
      const openingDate = new Date(tender.openingDate);
      const now = new Date();
      
      return now >= openingDate;
    }
    
    return false;
  }, [tender]);

  const handleOpenTender = async () => {
    if (!id || !canOpenTender) return;
    
    try {
      await updateStage.mutateAsync({
        id,
        data: { stage: 'OPENED' },
      });
      refetch();
    } catch (error) {
      console.error("Error opening tender:", error);
    }
  };

  const handleStartTechnicalEvaluation = async () => {
    if (!id || tender?.stage !== 'OPENED') return;
    
    try {
      await updateStage.mutateAsync({
        id,
        data: { stage: 'TECH_EVAL' },
      });
      refetch();
      notifications.show({
        title: 'Success',
        message: 'Tender moved to Technical Evaluation stage',
        color: 'green',
      });
    } catch (error: any) {
      console.error("Error starting technical evaluation:", error);
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to start technical evaluation',
        color: 'red',
      });
    }
  };

  const handleCompleteTechnicalEvaluation = async () => {
    if (!id || tender?.stage !== 'TECH_EVAL') return;
    
    try {
      await updateStage.mutateAsync({
        id,
        data: { stage: 'FIN_EVAL' },
      });
      refetch();
      notifications.show({
        title: 'Success',
        message: 'Technical evaluation completed. Tender moved to Financial Evaluation stage',
        color: 'green',
      });
    } catch (error: any) {
      console.error("Error completing technical evaluation:", error);
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to complete technical evaluation',
        color: 'red',
      });
    }
  };

  const handleStartTenderEvaluation = async () => {
    if (!id || tender?.stage !== 'OPENED') return;
    
    // Check if all bid submissions have qualification status updated
    const allQualified = bidSubmissions && bidSubmissions.length > 0 && 
      bidSubmissions.every((submission: any) => 
        submission.qualificationStatus === 'QUALIFIED' || submission.qualificationStatus === 'DISQUALIFIED'
      );
    
    if (!allQualified) {
      notifications.show({
        title: 'Cannot Start Evaluation',
        message: 'Please update qualification status for all bid submissions before starting evaluation',
        color: 'yellow',
      });
      return;
    }
    
    try {
      // Determine next stage based on technical evaluation requirement
      const nextStage = tender.technicalEvaluationRequired ? 'TECH_EVAL' : 'FIN_EVAL';
      
      await updateStage.mutateAsync({
        id,
        data: { stage: nextStage },
      });
      refetch();
      notifications.show({
        title: 'Success',
        message: `Tender evaluation started. Moved to ${nextStage === 'TECH_EVAL' ? 'Technical Evaluation' : 'Financial Evaluation'} stage`,
        color: 'green',
      });
    } catch (error: any) {
      console.error("Error starting tender evaluation:", error);
      notifications.show({
        title: 'Error',
        message: error.response?.data?.error || 'Failed to start tender evaluation',
        color: 'red',
      });
    }
  };

  // Check if all bid submissions have qualification status updated
  const allSubmissionsQualified = useMemo(() => {
    if (!bidSubmissions || bidSubmissions.length === 0) return false;
    return bidSubmissions.every((submission: any) => 
      submission.qualificationStatus === 'QUALIFIED' || submission.qualificationStatus === 'DISQUALIFIED'
    );
  }, [bidSubmissions]);

  // Check if can start evaluation (OPENED stage and all submissions qualified)
  const canStartEvaluation = tender?.stage === 'OPENED' && allSubmissionsQualified && bidSubmissions && bidSubmissions.length > 0;

  // Initialize evaluator emails from tender data
  useEffect(() => {
    if (tender?.technicalEvaluatorEmails) {
      // Handle both array and JSON string formats
      let emailArray: string[] = [];
      if (Array.isArray(tender.technicalEvaluatorEmails)) {
        emailArray = tender.technicalEvaluatorEmails;
      } else if (typeof tender.technicalEvaluatorEmails === 'string') {
        try {
          emailArray = JSON.parse(tender.technicalEvaluatorEmails);
        } catch (e) {
          console.error('Failed to parse technicalEvaluatorEmails:', e);
          emailArray = [];
        }
      }
      setEvaluatorEmails(emailArray.join(", "));
    } else {
      setEvaluatorEmails("");
    }
  }, [tender]);

  // Check if tender is in OPENED stage or later
  const canEditEmails = tender && ['OPENED', 'TECH_EVAL', 'FIN_EVAL', 'AWARDED'].includes(tender.stage);

  const handleSaveEvaluatorEmails = async () => {
    try {
      // Validate emails
      const emailList = evaluatorEmails
        .split(',')
        .map(email => email.trim())
        .filter(email => email.length > 0);

      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const invalidEmails = emailList.filter(email => !emailRegex.test(email));
      
      if (invalidEmails.length > 0) {
        notifications.show({
          title: "Validation Error",
          message: `Invalid email format: ${invalidEmails.join(", ")}`,
          color: "red",
        });
        return;
      }

      await updateTender.mutateAsync({
        id: id!,
        data: {
          technicalEvaluatorEmails: emailList,
        },
      });

      // Update local state immediately with saved emails
      setEvaluatorEmails(emailList.join(", "));
      setIsEditingEmails(false);
      
      notifications.show({
        title: "Success",
        message: `Technical evaluator emails saved successfully. ${emailList.length} evaluator(s) will receive evaluation requests.`,
        color: "green",
      });
      
      // Refetch to get latest data from server
      await refetch();
      
      // Wait a moment for backend to create evaluations, then refetch evaluations
      // The backend creates evaluations asynchronously, so we poll a few times
      let attempts = 0;
      const maxAttempts = 5;
      const pollInterval = 1000; // 1 second
      
      const pollEvaluations = setInterval(async () => {
        attempts++;
        await refetchEvaluations();
        
        if (attempts >= maxAttempts) {
          clearInterval(pollEvaluations);
        }
      }, pollInterval);
      
      // Clear interval after max attempts
      setTimeout(() => {
        clearInterval(pollEvaluations);
      }, maxAttempts * pollInterval);
    } catch (error: any) {
      console.error("Error saving evaluator emails:", error);
      notifications.show({
        title: "Error",
        message: error.response?.data?.error || "Failed to save evaluator emails",
        color: "red",
      });
    }
  };

  if (isLoading) {
    return (
      <Container size="xl" py="xl">
        <Text>Loading tender details...</Text>
      </Container>
    );
  }

  if (error || !tender) {
    return (
      <Container size="xl" py="xl">
        <Text color="red">Failed to load tender details</Text>
      </Container>
    );
  }


  return (
    <Container size="xl" py="xl">
      <Paper shadow="sm" p="xl">
        <Group mb="xl">
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate("/tenders")}
          >
            Back to Tenders
          </Button>
        </Group>

        <Group justify="space-between" mb="xl">
          <div>
            <Title order={2}>{tender.description}</Title>
            <Text c="dimmed" size="sm">
              {tender.referenceNo} • Created on{" "}
              {dayjs(tender.createdAt).format("MMM DD, YYYY")}
            </Text>
          </div>
          <Group>
            <StageBadge stage={tender.stage} />
            {canOpenTender && (
              <Button
                variant="filled"
                color="green"
                leftSection={<IconLockOpen size={16} />}
                onClick={handleOpenTender}
                loading={updateStage.isPending}
              >
                Open Tender
              </Button>
            )}
            {tender.stage === 'OPENED' && (
              <Button
                variant="filled"
                color="blue"
                leftSection={<IconAward size={16} />}
                onClick={handleStartTechnicalEvaluation}
                loading={updateStage.isPending}
              >
                Start Technical Evaluation
              </Button>
            )}
            <Button
              leftSection={<IconEdit size={16} />}
              variant="light"
              onClick={() => navigate(`/tenders/${tender.id}/edit`)}
            >
              Edit
            </Button>
          </Group>
        </Group>

        <Tabs value={activeTab} onChange={setActiveTab}>
          <Tabs.List>
            <Tabs.Tab value="overview" leftSection={<IconFileText size={16} />}>
              Overview
            </Tabs.Tab>
            <Tabs.Tab
              value="tor"
              leftSection={<IconFileDescription size={16} />}
            >
              TOR
            </Tabs.Tab>
            <Tabs.Tab value="bid-submissions" leftSection={<IconUsers size={16} />}>
              Bid Submissions
            </Tabs.Tab>
            {canViewEvaluations && tender.technicalEvaluationRequired && (
              <Tabs.Tab value="technical-evaluation" leftSection={<IconAward size={16} />}>
                Technical Evaluation
              </Tabs.Tab>
            )}
            {canViewEvaluations && (
              <Tabs.Tab value="financial-evaluation" leftSection={<IconCurrencyDollar size={16} />}>
                Overall Evaluation
              </Tabs.Tab>
            )}
            {canReviewBids && (
              <Tabs.Tab value="bid-comparison" leftSection={<IconAward size={16} />}>
                Bid Comparison
              </Tabs.Tab>
            )}
            <Tabs.Tab
              value="documents"
              leftSection={<IconFileText size={16} />}
            >
              Documents
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="overview" pt="xl">
                <Stack gap="xl">
                  {/* Basic Information & Timeline Combined */}
              <Card shadow="md" p="md" withBorder>
                <Stack gap="md">
                  {/* Basic Information Section */}
                  <Group grow>
                    <div>
                      <Text size="sm" c="dimmed">
                        Category
                      </Text>
                      <Badge variant="light">{tender.category}</Badge>
                    </div>
                    <div>
                      <Text size="sm" c="dimmed">
                        Awarded Vendor
                      </Text>
                      <Text size="sm">
                        {tender.awardedVendor || "Not awarded yet"}
                      </Text>
                    </div>
                    <div>
                      <Text size="sm" c="dimmed">
                        Vendor Registration Required
                      </Text>
                      <Badge color={tender.requireRegistration ? "orange" : "gray"} size="sm">
                        {tender.requireRegistration ? "Yes" : "No"}
                      </Badge>
                    </div>
                  </Group>

                  {/* Divider */}
                  <div style={{ height: '1px', backgroundColor: '#e9ecef', margin: '8px 0' }} />

                  {/* Compact Timeline */}
                  <div style={{ width: '100%', padding: '4px 0' }}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      justifyContent: 'space-between',
                      width: '100%',
                      position: 'relative',
                    }}>
                      {/* Timeline items */}
                      {(() => {
                        const timelineItems: Array<{
                          date: Date;
                          label: string;
                          description?: string;
                          color: string;
                        }> = [];

                        // Add pre-bid meeting if exists
                        if (tender.requirePrebidMeeting && tender.prebidMeetingDate) {
                          timelineItems.push({
                            date: new Date(tender.prebidMeetingDate),
                            label: 'Pre-Bid Meeting',
                            description: tender.prebidMeetingVenue || undefined,
                            color: 'blue',
                          });
                        }

                        // Add publishing date
                        timelineItems.push({
                          date: new Date(tender.publishingDate),
                          label: 'Published',
                          color: 'green',
                        });

                        // Add submission deadline
                        timelineItems.push({
                          date: new Date(tender.submissionDeadline),
                          label: 'Submission Deadline',
                          color: 'orange',
                        });

                        // Add opening date if exists
                        if (tender.openingDate) {
                          timelineItems.push({
                            date: new Date(tender.openingDate),
                            label: 'Opening',
                            color: 'purple',
                          });
                        }

                        // Sort by date
                        timelineItems.sort((a, b) => a.date.getTime() - b.date.getTime());

                        const now = new Date();
                        
                        return timelineItems.map((item, index) => {
                          const isPast = item.date < now;
                          const isLast = index === timelineItems.length - 1;
                          
                          return (
                            <div key={index} style={{ 
                              display: 'flex', 
                              flexDirection: 'column', 
                              alignItems: 'center',
                              position: 'relative',
                              flex: 1,
                              maxWidth: '100%'
                            }}>
                              {/* Connector line */}
                              {!isLast && (
                                <div style={{
                                  position: 'absolute',
                                  top: '10px',
                                  left: '50%',
                                  right: '-50%',
                                  height: '2px',
                                  backgroundColor: isPast ? '#228be6' : '#e9ecef',
                                  zIndex: 0,
                                }} />
                              )}
                              
                              {/* Date circle - smaller */}
                              <div style={{
                                width: '20px',
                                height: '20px',
                                borderRadius: '50%',
                                backgroundColor: isPast ? '#228be6' : '#e9ecef',
                                border: `2px solid ${isPast ? '#228be6' : '#dee2e6'}`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative',
                                zIndex: 1,
                                marginBottom: '4px'
                              }}>
                                <div style={{
                                  width: '6px',
                                  height: '6px',
                                  borderRadius: '50%',
                                  backgroundColor: isPast ? '#fff' : '#adb5bd'
                                }} />
                              </div>
                              
                              {/* Date - compact */}
                              <Text size="xs" fw={600} mb={1} style={{ textAlign: 'center', lineHeight: 1.1, fontSize: '10px' }}>
                                {dayjs(item.date).format("MMM DD")}
                              </Text>
                              
                              {/* Label - compact */}
                              <Badge 
                                size="xs" 
                                variant={isPast ? "filled" : "light"}
                                color={item.color}
                                mb={item.description ? 1 : 0}
                                style={{ textAlign: 'center', whiteSpace: 'normal', fontSize: '9px', padding: '1px 4px', lineHeight: 1.2 }}
                              >
                                {item.label}
                              </Badge>
                              
                              {/* Description - compact */}
                              {item.description && (
                                <Text size="xs" c="dimmed" style={{ textAlign: 'center', marginTop: '1px', fontSize: '9px', lineHeight: 1.1 }}>
                                  {item.description}
                                </Text>
                              )}
                              
                              {/* Time - compact */}
                              <Text size="xs" c="dimmed" mt={1} style={{ textAlign: 'center', fontSize: '9px', lineHeight: 1.1 }}>
                                {dayjs(item.date).format("HH:mm")}
                              </Text>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                </Stack>
              </Card>

                  {/* Tender Items */}
                  <Card shadow="sm" p="lg">
                    <Title order={3} mb="md" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <IconPackage size={20} />
                      Tender Items ({tenderItems?.length || 0})
                    </Title>
                    {loadingItems ? (
                      <Text c="dimmed">Loading tender items...</Text>
                    ) : tenderItems && tenderItems.length > 0 ? (
                      <Table striped highlightOnHover>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>#</Table.Th>
                            <Table.Th>Item Name</Table.Th>
                            <Table.Th>Description</Table.Th>
                            <Table.Th>Quantity</Table.Th>
                            <Table.Th>Unit</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                        {tenderItems.map((item) => (
                            <Table.Tr key={item.id}>
                              <Table.Td>
                              <Badge size="sm" variant="light">
                                  {item.order}
                              </Badge>
                              </Table.Td>
                              <Table.Td>
                                <Text fw={500}>{item.itemName}</Text>
                              </Table.Td>
                              <Table.Td>
                                <Text size="sm" c={item.description ? undefined : "dimmed"}>
                                  {item.description || "-"}
                              </Text>
                              </Table.Td>
                              <Table.Td>
                                <Text>{item.quantity}</Text>
                              </Table.Td>
                              <Table.Td>
                                <Text>{item.unit}</Text>
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    ) : (
                      <Text c="dimmed">No tender items defined</Text>
                    )}
                  </Card>

                  {/* Required Documents */}
                  <Card shadow="sm" p="lg">
                    <Title order={3} mb="md" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <IconClipboardList size={20} />
                      Required Documents ({tenderRequirements?.length || 0})
                    </Title>
                    {loadingRequirements ? (
                      <Text c="dimmed">Loading document requirements...</Text>
                    ) : tenderRequirements && tenderRequirements.length > 0 ? (
                      <Table striped highlightOnHover>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>#</Table.Th>
                            <Table.Th>Document Type</Table.Th>
                            <Table.Th>Description</Table.Th>
                            <Table.Th>Required</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                        {tenderRequirements.map((requirement) => (
                            <Table.Tr key={requirement.id}>
                              <Table.Td>
                                <Badge size="sm" variant="light">
                                  {requirement.order}
                                </Badge>
                              </Table.Td>
                              <Table.Td>
                              <Text fw={500}>
                                {requirement.documentType === 'CUSTOM' 
                                  ? requirement.description 
                                  : requirement.documentType.replace(/_/g, ' ')}
                              </Text>
                              </Table.Td>
                              <Table.Td>
                                <Text size="sm" c={requirement.description && requirement.documentType !== 'CUSTOM' ? undefined : "dimmed"}>
                                  {requirement.description && requirement.documentType !== 'CUSTOM' 
                                    ? requirement.description 
                                    : "-"}
                              </Text>
                              </Table.Td>
                              <Table.Td>
                                {requirement.isRequired ? (
                                  <Badge size="sm" color="red">Required</Badge>
                                ) : (
                                  <Badge size="sm" variant="light" color="gray">Optional</Badge>
                                )}
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    ) : (
                      <Text c="dimmed">No document requirements defined</Text>
                    )}
                  </Card>

                  {/* Evaluation Criteria */}
                  <Card shadow="sm" p="lg">
                    <Title order={3} mb="md" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <IconAward size={20} />
                      Evaluation Criteria ({tender.evaluationCriteria?.length || 0})
                    </Title>
                    {tender.evaluationCriteria && tender.evaluationCriteria.length > 0 ? (
                      <Table striped highlightOnHover>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>#</Table.Th>
                            <Table.Th>Criteria Name</Table.Th>
                            <Table.Th>Description</Table.Th>
                            <Table.Th>Weightage</Table.Th>
                            <Table.Th>Type</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                        {tender.evaluationCriteria.map((criteria: any) => (
                            <Table.Tr key={criteria.id}>
                              <Table.Td>
                                <Badge size="sm" variant="light">
                                  {criteria.order}
                                </Badge>
                              </Table.Td>
                              <Table.Td>
                              <Text fw={500}>{criteria.criteriaName}</Text>
                              </Table.Td>
                              <Table.Td>
                                <Text size="sm" c={criteria.description ? undefined : "dimmed"}>
                                  {criteria.description || "-"}
                                </Text>
                              </Table.Td>
                              <Table.Td>
                                <Badge size="sm" color="blue">{criteria.weightage}%</Badge>
                              </Table.Td>
                              <Table.Td>
                                {criteria.isDefault ? (
                                  <Badge size="sm" color="green">Default</Badge>
                                ) : (
                                  <Badge size="sm" variant="light" color="gray">Custom</Badge>
                                )}
                              </Table.Td>
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    ) : (
                      <Text c="dimmed">No evaluation criteria defined</Text>
                    )}
                  </Card>
                </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="tor" pt="xl">
            <TorTab
              tenderId={tender.id}
              torSpecification={tender.torSpecification}
              tenderStage={tender.stage}
            />
          </Tabs.Panel>

          <Tabs.Panel value="bid-submissions" pt="xl">
            <Card shadow="sm" p="lg">
              <Group justify="space-between" mb="md">
                <Title order={3}>
                  Bid Submissions ({bidSubmissions?.length || 0})
                </Title>
                {canStartEvaluation && (
                  <Button
                    leftSection={<IconAward size={16} />}
                    onClick={handleStartTenderEvaluation}
                    loading={updateStage.isPending}
                    color="green"
                  >
                    Start Tender Evaluation
                  </Button>
                )}
              </Group>
              
              {bidSubmissions && bidSubmissions.length > 0 ? (
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Company Name</Table.Th>
                      <Table.Th>Representative</Table.Th>
                      {canReviewBids && <Table.Th>Total Price</Table.Th>}
                      <Table.Th>Bid Items</Table.Th>
                      <Table.Th>Submitted At</Table.Th>
                      <Table.Th>Documents</Table.Th>
                      {canReviewBids && <Table.Th>Qualification Status</Table.Th>}
                      {canReviewBids && <Table.Th>Qualification Notes</Table.Th>}
                      <Table.Th>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {bidSubmissions.map((submission) => (
                      <Table.Tr key={submission.id}>
                        <Table.Td>
                          <Text fw={500}>{submission.companyName}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{submission.representativeName || "-"}</Text>
                          {submission.representativeEmail && (
                            <Text size="xs" c="dimmed">
                              {submission.representativeEmail}
                            </Text>
                          )}
                        </Table.Td>
                        {canReviewBids && (
                          <Table.Td>{formatCurrency(Number(submission.totalPrice), tender?.currency || 'MVR')}</Table.Td>
                        )}
                        <Table.Td>
                          <Badge size="sm" variant="light">
                            {submission.bidItems?.length || 0} items
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {dayjs(submission.submittedAt).format("MMM DD, YYYY HH:mm")}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Stack gap="xs">
                            {submission.hasTOR && <Badge size="xs" color="green">TOR</Badge>}
                            {submission.hasFinancialStatement && <Badge size="xs" color="blue">Financial</Badge>}
                            {submission.hasTaxClearance && <Badge size="xs" color="orange">Tax</Badge>}
                            {submission.hasInsurance && <Badge size="xs" color="purple">Insurance</Badge>}
                            {submission.hasExperience && <Badge size="xs" color="teal">Experience</Badge>}
                            {submission.hasTechnicalProposal && <Badge size="xs" color="indigo">Technical</Badge>}
                            {submission.hasFinancialProposal && <Badge size="xs" color="pink">Financial Prop</Badge>}
                          </Stack>
                        </Table.Td>
                        {canReviewBids && (
                          <Table.Td>
                            <Badge
                              color={
                                submission.qualificationStatus === 'QUALIFIED' ? 'green' :
                                submission.qualificationStatus === 'DISQUALIFIED' ? 'red' : 'gray'
                              }
                              size="sm"
                            >
                              {submission.qualificationStatus || 'PENDING'}
                            </Badge>
                          </Table.Td>
                        )}
                        {canReviewBids && (
                          <Table.Td>
                            {submission.qualificationNotes ? (
                              <Text size="xs" c="dimmed" lineClamp={2}>
                                {submission.qualificationNotes}
                              </Text>
                            ) : (
                              <Text size="xs" c="dimmed">-</Text>
                            )}
                          </Table.Td>
                        )}
                        <Table.Td>
                          <Group gap="xs">
                            {canReviewBids && (
                              <Button
                                variant="light"
                                size="xs"
                                color={submission.qualificationStatus === 'QUALIFIED' ? 'green' : submission.qualificationStatus === 'DISQUALIFIED' ? 'red' : 'blue'}
                                onClick={() => {
                                  setSelectedBidSubmission(submission);
                                  setQualificationModalOpened(true);
                                }}
                              >
                                {submission.qualificationStatus ? 'Review' : 'Review & Qualify'}
                              </Button>
                            )}
                            <Button
                              variant="light"
                              size="xs"
                              disabled={!canReviewBids}
                              onClick={() => navigate(`/tenders/${tender.id}/bid-submissions/${submission.id}`)}
                            >
                              View Details
                            </Button>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              ) : (
                <Text c="dimmed">No bid submissions received yet</Text>
              )}
            </Card>
          </Tabs.Panel>

          {tender.technicalEvaluationRequired && (
            <Tabs.Panel value="technical-evaluation" pt="xl">
              <Stack gap="md">
                {/* Technical Evaluator Emails Section */}
                {canEditEmails && (
                  <Card shadow="sm" p="lg">
                    <Title order={4} mb="md">Technical Evaluator Emails</Title>
                    <Text size="sm" c="dimmed" mb="md">
                      Enter the email addresses of technical evaluators who will review bid submissions. 
                      Separate multiple emails with commas.
                    </Text>
                    {!isEditingEmails ? (
                      <Stack gap="sm">
                        {evaluatorEmails ? (
                          <Text size="sm">
                            <strong>Current evaluators:</strong> {evaluatorEmails}
                          </Text>
                        ) : (
                          <Alert color="blue" variant="light">
                            No technical evaluator emails have been set yet.
                          </Alert>
                        )}
                        <Button
                          variant="light"
                          onClick={() => setIsEditingEmails(true)}
                        >
                          {evaluatorEmails ? "Edit Emails" : "Add Evaluator Emails"}
                        </Button>
                      </Stack>
                    ) : (
                      <Stack gap="md">
                        <Textarea
                          label="Technical Evaluator Emails"
                          placeholder="evaluator1@example.com, evaluator2@example.com"
                          value={evaluatorEmails}
                          onChange={(e) => setEvaluatorEmails(e.target.value)}
                          rows={3}
                        />
                        <Group>
                          <Button
                            onClick={handleSaveEvaluatorEmails}
                            loading={updateTender.isPending}
                          >
                            Save Emails
                          </Button>
                          <Button
                            variant="subtle"
                            onClick={() => {
                              setIsEditingEmails(false);
                              // Reset to original value from tender
                              if (tender?.technicalEvaluatorEmails) {
                                let emailArray: string[] = [];
                                if (Array.isArray(tender.technicalEvaluatorEmails)) {
                                  emailArray = tender.technicalEvaluatorEmails;
                                } else if (typeof tender.technicalEvaluatorEmails === 'string') {
                                  try {
                                    emailArray = JSON.parse(tender.technicalEvaluatorEmails);
                                  } catch (e) {
                                    emailArray = [];
                                  }
                                }
                                setEvaluatorEmails(emailArray.join(", "));
                              } else {
                                setEvaluatorEmails("");
                              }
                            }}
                          >
                            Cancel
                          </Button>
                        </Group>
                      </Stack>
                    )}
                  </Card>
                )}

                {/* Technical Evaluations List */}
              <Card shadow="sm" p="lg">
                <Group justify="space-between" mb="md">
                  <Title order={3}>
                      Technical Evaluations ({technicalEvaluations?.length || 0})
                  </Title>
                  <Group gap="xs">
                    <Button
                      leftSection={<IconAward size={16} />}
                      onClick={() => createBulkEvaluations.mutate(id!)}
                      loading={createBulkEvaluations.isPending}
                      disabled={!bidSubmissions || bidSubmissions.filter((s: any) => s.qualificationStatus === 'QUALIFIED').length === 0}
                    >
                      Create Evaluations
                    </Button>
                    {tender?.stage === 'TECH_EVAL' && (
                      <Button
                        leftSection={<IconCheck size={16} />}
                        onClick={handleCompleteTechnicalEvaluation}
                        loading={updateStage.isPending}
                        color="green"
                      >
                        Complete Technical Evaluation
                      </Button>
                    )}
                  </Group>
                </Group>
                
                {loadingEvaluations ? (
                  <Text c="dimmed">Loading technical evaluations...</Text>
                ) : technicalEvaluations && technicalEvaluations.length > 0 ? (
                  <Table>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Company</Table.Th>
                        <Table.Th>Status</Table.Th>
                        <Table.Th>Evaluator</Table.Th>
                        <Table.Th>Decision</Table.Th>
                        <Table.Th>Actions</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {technicalEvaluations.map((evaluation: any) => (
                        <Table.Tr key={evaluation.id}>
                          <Table.Td>
                            <Text fw={500}>{evaluation.bidSubmission?.companyName}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Badge 
                              size="sm" 
                              color={
                                evaluation.status === 'APPROVED' ? 'green' :
                                evaluation.status === 'REJECTED' ? 'red' :
                                evaluation.status === 'COMPLETED' ? 'blue' :
                                evaluation.status === 'IN_PROGRESS' ? 'yellow' : 'gray'
                              }
                            >
                              {evaluation.status}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">
                              {evaluation.evaluator?.fullName || evaluation.evaluatorEmail || "-"}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            {evaluation.isApproved !== undefined ? (
                              <Badge 
                                size="sm" 
                                color={evaluation.isApproved ? 'green' : 'red'}
                              >
                                {evaluation.isApproved ? 'Approved' : 'Rejected'}
                              </Badge>
                            ) : (
                              <Text size="sm" c="dimmed">-</Text>
                            )}
                          </Table.Td>
                          <Table.Td>
                            <Button
                              size="xs"
                              variant="light"
                              onClick={() => navigate(`/tenders/${id}/technical-evaluations/${evaluation.id}`)}
                            >
                              Review
                            </Button>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                ) : (
                  <Text c="dimmed">
                    No technical evaluations found. Click "Create Evaluations" to start the technical evaluation process.
                  </Text>
                )}
              </Card>
              </Stack>
            </Tabs.Panel>
          )}

          {canViewEvaluations && (
            <Tabs.Panel value="financial-evaluation" pt="xl">
              <Stack gap="md">
                {tender?.stage === 'FIN_EVAL' && (
                  <Card shadow="sm" p="md" withBorder>
                    <Group justify="space-between">
                      <div>
                        <Text fw={500} mb="xs">Evaluation Report</Text>
                        {evaluationReport ? (
                          <Group gap="xs">
                            <Badge
                              color={
                                evaluationReport.status === 'APPROVED'
                                  ? 'green'
                                  : evaluationReport.status === 'REJECTED'
                                  ? 'red'
                                  : evaluationReport.status === 'PENDING_CHECK'
                                  ? 'yellow'
                                  : evaluationReport.status === 'PENDING_APPROVAL'
                                  ? 'orange'
                                  : 'blue'
                              }
                            >
                              {evaluationReport.status.replace('_', ' ')}
                            </Badge>
                            <Button
                              size="xs"
                              variant="light"
                              onClick={() => navigate(`/tenders/${id}/evaluation-report`)}
                            >
                              View Report
                            </Button>
                          </Group>
                        ) : (
                          <Button
                            size="xs"
                            onClick={() => setGenerateReportModalOpened(true)}
                          >
                            Generate Report
                          </Button>
                        )}
                      </div>
                    </Group>
                  </Card>
                )}
                <OverallEvaluationTab
                  tenderId={id!}
                  tender={tender}
                  bidSubmissions={bidSubmissions || []}
                  technicalEvaluations={technicalEvaluations || []}
                  evaluationCriteria={evaluationCriteria || []}
                  loadingCriteria={loadingCriteria}
                  scoreData={scoreData}
                  setScoreData={setScoreData}
                  saveScores={saveScores}
                  bulkSaveScores={bulkSaveScores}
                  navigate={navigate}
                />
              </Stack>
            </Tabs.Panel>
          )}

          <GenerateEvaluationReportModal
            opened={generateReportModalOpened}
            onClose={() => setGenerateReportModalOpened(false)}
            tenderId={id!}
            onSuccess={() => {
              setGenerateReportModalOpened(false);
            }}
          />

          <Tabs.Panel value="bid-comparison" pt="xl">
            <Card shadow="sm" p="lg">
              <Title order={3} mb="md">
                Bid Items Comparison
              </Title>
              
              {bidSubmissions && bidSubmissions.length > 0 && tenderItems && tenderItems.length > 0 ? (
                (() => {
                  // Filter to only include QUALIFIED bid submissions
                  const qualifiedSubmissions = bidSubmissions.filter((s: any) => s.qualificationStatus === 'QUALIFIED');
                  
                  if (qualifiedSubmissions.length === 0) {
                    return (
                      <Text c="dimmed">
                        No qualified bid submissions found for comparison. Please qualify bid submissions first.
                      </Text>
                    );
                  }
                  
                  return (
                    <div style={{ overflowX: 'auto' }}>
                      <Table>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>Tender Item</Table.Th>
                            {qualifiedSubmissions.map((submission: any) => (
                              <Table.Th key={submission.id}>
                                <div>
                                  <Text size="sm" fw={500}>{submission.companyName}</Text>
                                  <Text size="xs" c="dimmed">{formatCurrency(Number(submission.totalPrice), tender?.currency || 'MVR')}</Text>
                                </div>
                              </Table.Th>
                            ))}
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {tenderItems.map((item) => (
                            <Table.Tr key={item.id}>
                              <Table.Td>
                                <div>
                                  <Text size="sm" fw={500}>{item.itemName}</Text>
                                  <Text size="xs" c="dimmed">{item.quantity} {item.unit}</Text>
                                </div>
                              </Table.Td>
                              {qualifiedSubmissions.map((submission: any) => {
                                const bidItem = submission.bidItems?.find((bi: any) => bi.tenderItemId === item.id);
                                return (
                                  <Table.Td key={submission.id}>
                                    {bidItem ? (
                                      <div>
                                        <Text size="sm" fw={500}>
                                          {formatCurrency(Number(bidItem.price), tender?.currency || 'MVR')}
                                        </Text>
                                        <Text size="xs" c="dimmed">
                                          {bidItem.quantity} {bidItem.unit}
                                        </Text>
                                        <Text size="xs" color="blue">
                                          Subtotal: {formatCurrency(Number(bidItem.price) * bidItem.quantity, tender?.currency || 'MVR')}
                                        </Text>
                                      </div>
                                    ) : (
                                      <Text size="sm" c="dimmed">-</Text>
                                    )}
                                  </Table.Td>
                                );
                              })}
                            </Table.Tr>
                          ))}
                          <Table.Tr style={{ backgroundColor: '#f8f9fa' }}>
                            <Table.Td>
                              <Text size="sm" fw={600}>TOTAL</Text>
                            </Table.Td>
                            {qualifiedSubmissions.map((submission: any) => (
                              <Table.Td key={submission.id}>
                                <Text size="sm" fw={600} color="blue">
                                  {formatCurrency(Number(submission.totalPrice), tender?.currency || 'MVR')}
                                </Text>
                              </Table.Td>
                            ))}
                          </Table.Tr>
                        </Table.Tbody>
                      </Table>
                    </div>
                  );
                })()
              ) : (
                <Text c="dimmed">
                  {!bidSubmissions || bidSubmissions.length === 0 
                    ? "No bid submissions to compare" 
                    : "No tender items defined for comparison"}
                </Text>
              )}
            </Card>
          </Tabs.Panel>

          <Tabs.Panel value="documents" pt="xl">
            <Card shadow="sm" p="lg">
              <Title order={3} mb="md">
                Documents
              </Title>
              {tender.documents && tender.documents.length > 0 ? (
                <Stack gap="sm">
                  {tender.documents.map((doc) => (
                    <Group key={doc.id} justify="space-between">
                      <Text size="sm" lineClamp={1}>
                        {doc.fileName}
                      </Text>
                      <Badge size="xs" variant="light">
                        {doc.type}
                      </Badge>
                    </Group>
                  ))}
                </Stack>
              ) : (
                <Text c="dimmed" size="sm">
                  No documents uploaded
                </Text>
              )}
            </Card>
          </Tabs.Panel>
        </Tabs>
      </Paper>

      <BidQualificationModal
        opened={qualificationModalOpened}
        onClose={() => {
          setQualificationModalOpened(false);
          setSelectedBidSubmission(null);
        }}
        bidSubmission={selectedBidSubmission}
        tenderId={tender.id}
      />
    </Container>
  );
}
